export type ThediracGeologySite = {
  id: string
  shortName: string
  name: string
  lon: number
  lat: number
  driveMinutes: number
  driveKm: number
  inpgCode?: string
  stars?: number
  materialOrAge: string
  evidenceNl: string
  terrainNl: string
  surfaceNl: string
  accessNl: string
  collectingNl: string
  protectionNl?: string
  coordinateBasisNl: string
  officialUrl?: string
  protectionUrl?: string
  sourceUrl?: string
}

const INPG_CONTOUR = 'Representatief centrum van de openbare INPG-contour; geen ingang, parkeerplaats of toegangstoestemming.'
const PERMISSION_ONLY = 'Alleen zoeken of meenemen met uitdrukkelijke toestemming van eigenaar/beheerder en na controle van lokale bescherming.'
const NO_MINE_ENTRY = 'Geen publieke toegang bevestigd. Betreed nooit een groeve, mijn, schacht of grot buiten een officieel bezoekerspad.'
const RNN_NO_COLLECTING = 'Verboden: in de Réserve naturelle nationale géologique du Lot zijn prospecteren, hakken en meenemen van gesteente, mineralen of fossielen niet toegestaan.'
const RNN_STATUS = 'Réserve naturelle nationale géologique du Lot; veel deelgebieden zijn bovendien privéterrein.'
const RNN_URL = 'https://www.parc-causses-du-quercy.fr/comprendre-le-parc/rnn/'
const INPG_DATASET_URL = 'https://catalogue.picto-occitanie.fr/geonetwork/srv/api/records/41d5964b-8c16-438e-8b59-a510362a72cf'

// De rijtijden zijn OSRM-richtwaarden vanaf het centrum van Thédirac, zonder
// actuele verkeersdrukte. De INPG-markers zijn centra van openbare broncontouren
// en mogen dus niet als ingang, parkeerplaats of toestemming worden gelezen.
export const THEDIRAC_MINERAL_SITES: ThediracGeologySite[] = [
  {
    id: 'min-pied-noir',
    shortName: 'Pied Noir',
    name: 'Grès quartzitiques du Pied Noir · Gourdon',
    lon: 1.339207,
    lat: 44.725854,
    driveMinutes: 22,
    driveKm: 19.3,
    inpgCode: 'MPY0053',
    stars: 1,
    materialOrAge: 'Eocene kwartsitische zandsteen',
    evidenceNl: 'Gevalideerde INPG-geosite voor continentale sedimentatie en sedimentologie.',
    terrainNl: 'Natuurlijke geosite aan het oppervlak.',
    surfaceNl: 'De rots is bovengronds ontsloten; losse verzamelbare kristallen worden door de bron niet beloofd.',
    accessNl: 'Observeer alleen vanaf legaal toegankelijk terrein; de contour is geen aangewezen bezoekersingang.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'min-chateau-gourdon',
    shortName: 'Château Gourdon',
    name: 'Grès quartzitiques du château · Gourdon',
    lon: 1.38203,
    lat: 44.737726,
    driveMinutes: 27,
    driveKm: 23.6,
    inpgCode: 'MPY0145',
    stars: 2,
    materialOrAge: 'Eocene kwartsitische zandsteen',
    evidenceNl: 'Gevalideerde INPG-geosite met erosievormen in harde kwartsitische zandsteen.',
    terrainNl: 'Antropogene geosite aan het oppervlak.',
    surfaceNl: 'Zichtbare rotsvorm; geen garantie op losse of verzamelbare stukken.',
    accessNl: 'Alleen bekijken vanaf openbaar toegankelijke delen rond Gourdon.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'min-grotte-roland',
    shortName: 'Grotte de Roland',
    name: 'Kristallisaties van Grotte de Roland · Montcuq',
    lon: 1.193048,
    lat: 44.366561,
    driveMinutes: 53,
    driveKm: 36.9,
    inpgCode: 'MPY0279',
    stars: 2,
    materialOrAge: 'Grotkristallisaties en concreties',
    evidenceNl: 'INPG waardeert de natuurlijke grot om kristallisatie en mineralogie.',
    terrainNl: 'Ondergrondse natuurlijke grot.',
    surfaceNl: 'Ondergronds; dit is geen vindplek voor vrijliggende oppervlaktestukken.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: 'Niets aanraken of meenemen; alleen betreden wanneer een bevoegde beheerder dat officieel toestaat.',
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'min-grottes-lacave',
    shortName: 'Grottes de Lacave',
    name: 'Kristallisaties van de Grottes de Lacave',
    lon: 1.55799,
    lat: 44.84596,
    driveMinutes: 55,
    driveKm: 49.4,
    inpgCode: 'MPY0271',
    stars: 2,
    materialOrAge: 'Grotkristallisaties en concreties',
    evidenceNl: 'Gevalideerde INPG-grot met mineralogisch belang door kristallisatie.',
    terrainNl: 'Ondergrondse natuurlijke grot met officieel bezoekersparcours.',
    surfaceNl: 'Alleen in situ te bekijken tijdens het grotbezoek.',
    accessNl: 'Officiële betaalde rondleiding; opening en reservering vooraf controleren.',
    collectingNl: 'Uitsluitend kijken: niets aanraken, afbreken of meenemen.',
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://www.les-grottes-de-lacave.com/'
  },
  {
    id: 'min-pech-merle-calcite',
    shortName: 'Calciet Pech Merle',
    name: 'Zandhoudende calciet · Pech Merle',
    lon: 1.644273,
    lat: 44.508189,
    driveMinutes: 61,
    driveKm: 39.4,
    materialOrAge: 'Zandhoudende calciet; tweelingkristallen van het type Bellecroix',
    evidenceNl: 'De geologische excursiegids van AGSE beschrijft hier kruis- en hartvormige calciettweelingen in een voormalige zandgroeve.',
    terrainNl: 'Voormalige zandgroeve nabij Pech Merle.',
    surfaceNl: 'Historisch aan het oppervlak aangetroffen; actuele vondstkans wordt niet gegarandeerd.',
    accessNl: 'Geen verzamelterrein. De locatie is zwaar geplunderd en ligt binnen het beschermde reservaat.',
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: 'Publicatiecoördinaat uit de geologische excursiegids; geen bezoekersingang.',
    protectionUrl: RNN_URL,
    sourceUrl: 'https://agse-geologues.fr/documents/2025/Livret_guide_Quercy_EXCURSION_AGSE_2025.pdf'
  },
  {
    id: 'min-cloup-aural',
    shortName: 'Cloup d’Aural',
    name: 'Phosphatières du Cloup d’Aural · Bach',
    lon: 1.689675,
    lat: 44.350937,
    driveMinutes: 63,
    driveKm: 56.7,
    inpgCode: 'MPY0056',
    stars: 3,
    materialOrAge: 'Fosfaatrijke karstvulling / phosphatière',
    evidenceNl: 'Driesterren-INPG-site en voormalige fosfaatmijn; de fosfaatwinning maakte de fossielrijke karstvulling zichtbaar.',
    terrainNl: 'Voormalige ondergrondse mijn, ingericht als officiële bezoekerssite.',
    surfaceNl: 'De mijnstructuur en vulling zijn tijdens het bezoek zichtbaar; niet vrij verzamelen.',
    accessNl: 'Alleen via het officiële bezoekersparcours; opening en tickets vooraf controleren.',
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://phosphatieres.com/',
    protectionUrl: RNN_URL
  },
  {
    id: 'min-baraval',
    shortName: 'Baraval',
    name: 'Oligocene fosfaatvulling van Baraval · Bach',
    lon: 1.689865,
    lat: 44.348103,
    driveMinutes: 63,
    driveKm: 56.8,
    inpgCode: 'MPY0210',
    stars: 3,
    materialOrAge: 'Oligocene fosfaatrijke continentale vulling',
    evidenceNl: 'Driesterren-INPG-site voor sedimentologie van een fosfaathoudende karstvulling.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historische mijnvulling; geen bevestigde vrij toegankelijke ontsluiting.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: RNN_URL
  },
  {
    id: 'min-memerlin',
    shortName: 'Mémerlin',
    name: 'Phosphatière de Mémerlin · Larnagol',
    lon: 1.802284,
    lat: 44.488032,
    driveMinutes: 82,
    driveKm: 70.2,
    inpgCode: 'MPY0038',
    stars: 3,
    materialOrAge: 'Fosfaatrijke karstvulling / phosphatière',
    evidenceNl: 'Driesterren-INPG-site voor de historische fosfaatgrondstof in een karstholte.',
    terrainNl: 'Voormalige ondergrondse fosfaatmijn.',
    surfaceNl: 'Geen actuele openbare oppervlaktevondsten bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: RNN_URL
  },
  {
    id: 'min-raynal',
    shortName: 'Raynal',
    name: 'Ancienne phosphatière de Raynal · Saint-Antonin',
    lon: 1.706245,
    lat: 44.181562,
    driveMinutes: 79,
    driveKm: 77.7,
    inpgCode: 'MPY0313',
    stars: 1,
    materialOrAge: 'Fosfaatrijke karstvulling / oude phosphatière',
    evidenceNl: 'Gevalideerde INPG-locatie voor natuurlijke hulpbronnen in een voormalige fosfaatmijn.',
    terrainNl: 'Verlaten ondergrondse mijn.',
    surfaceNl: 'Historische winning is bewezen; de bron garandeert geen materiaal aan het huidige oppervlak.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'min-bretou',
    shortName: 'Mine du Bretou',
    name: 'IJzermijn du Bretou · Montricoux',
    lon: 1.649824,
    lat: 44.112226,
    driveMinutes: 82,
    driveKm: 87,
    inpgCode: 'MPY0339',
    stars: 1,
    materialOrAge: 'IJzererts gevormd door verwering',
    evidenceNl: 'Gevalideerde INPG-mijnlocatie met belang voor natuurlijke hulpbronnen en verweringsprocessen.',
    terrainNl: 'Verlaten ondergrondse ijzermijn.',
    surfaceNl: 'Historische winning is bewezen; geen openbare oppervlaktevondsten bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'min-maladrerie',
    shortName: 'La Maladrerie',
    name: 'Gemineraliseerde kwartsgang de la Maladrerie · Villefranche',
    lon: 2.036028,
    lat: 44.332803,
    driveMinutes: 92,
    driveKm: 85.9,
    inpgCode: 'MPY0701',
    stars: 2,
    materialOrAge: 'Stephanische gemineraliseerde kwartsgang',
    evidenceNl: 'Tweesterren-INPG-mijnsite met mineralogisch belang voor een gemineraliseerde kwartsgang.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historische mineralisatie is bewezen; actuele losse vondsten niet.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'min-la-baume',
    shortName: 'La Baume',
    name: 'Gemineraliseerde kwartsgang de la Baume · Villefranche',
    lon: 2.060192,
    lat: 44.339021,
    driveMinutes: 95,
    driveKm: 87.1,
    inpgCode: 'MPY0696',
    stars: 2,
    materialOrAge: 'Stephanische gemineraliseerde kwartsgang',
    evidenceNl: 'Tweesterren-INPG-mijnsite met mineralogisch belang voor een gemineraliseerde kwartsgang.',
    terrainNl: 'Voormalige ondergrondse mijn; net buiten de richttijd van 1½ uur.',
    surfaceNl: 'Historische mineralisatie is bewezen; actuele losse vondsten niet.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  }
]

export const THEDIRAC_FOSSIL_SITES: ThediracGeologySite[] = [
  {
    id: 'fos-plage-pterosaures',
    shortName: 'Plage aux Ptérosaures',
    name: 'La Plage aux Ptérosaures · Crayssac',
    lon: 1.325953,
    lat: 44.530253,
    driveMinutes: 22,
    driveKm: 15.8,
    inpgCode: 'MPY0055',
    stars: 3,
    materialOrAge: 'Laat-Jura, circa 150 miljoen jaar; voetsporen van dinosauriërs en pterosauriërs',
    evidenceNl: 'Driesterren-INPG-geosite met bewezen fossiele loopsporen op een oud kalkstrand.',
    terrainNl: 'Fossiele laag aan het oppervlak, beschermd onder een bezoekersgebouw.',
    surfaceNl: 'Ja, de originele spoorlaag is tijdens een rondleiding in situ zichtbaar.',
    accessNl: 'Alleen met officiële rondleiding en reservering; actuele openingsdata controleren.',
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://www.plageauxpterosaures.fr/',
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-mas-maury',
    shortName: 'Mas de Maury',
    name: 'Kimmeridgien–Tithonien du Mas de Maury',
    lon: 1.475854,
    lat: 44.700737,
    driveMinutes: 35,
    driveKm: 23.8,
    inpgCode: 'MPY0285',
    stars: 2,
    materialOrAge: 'Kimmeridgien–Tithonien, Laat-Jura',
    evidenceNl: 'Tweesterren-INPG-paleontologische ontsluiting in mariene platformafzettingen.',
    terrainNl: 'Natuurlijke ontsluiting aan het oppervlak.',
    surfaceNl: 'Ja, geologische lagen zijn bovengronds ontsloten; actuele losse fossielen worden niet gegarandeerd.',
    accessNl: 'Geen officiële bezoekersingang bevestigd; alleen vanaf legaal toegankelijk terrein observeren.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-bouzies',
    shortName: 'Bouziès',
    name: 'Eocene en kwartaire fossielvullingen · Bouziès',
    lon: 1.644547,
    lat: 44.478295,
    driveMinutes: 61,
    driveKm: 51.9,
    inpgCode: 'MPY0216',
    stars: 2,
    materialOrAge: 'Eocene en Kwartair; fossielhoudende karstvullingen',
    evidenceNl: 'Tweesterren-INPG-ontsluiting met paleontologisch belangrijke karstvullingen uit twee perioden.',
    terrainNl: 'Natuurlijke ontsluiting aan het oppervlak.',
    surfaceNl: 'De vulling is bovengronds ontsloten; zichtbaarheid kan door begroeiing of erosie veranderen.',
    accessNl: 'Geen officiële bezoekersingang bevestigd; terreinstatus ter plaatse respecteren.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-liauzu',
    shortName: 'Liauzu',
    name: 'Eocene fossielvulling du Liauzu · Orniac',
    lon: 1.675866,
    lat: 44.525929,
    driveMinutes: 59,
    driveKm: 44.2,
    inpgCode: 'MPY0235',
    stars: 1,
    materialOrAge: 'Eoceen; fossielhoudende karstvulling',
    evidenceNl: 'Gevalideerde INPG-paleontologische ontsluiting van een Eocene karstvulling.',
    terrainNl: 'Natuurlijke ontsluiting aan het oppervlak.',
    surfaceNl: 'Ja, de bron classificeert dit als een bovengrondse ontsluiting; vondstkans is niet gegarandeerd.',
    accessNl: 'Geen officiële bezoekersinrichting bevestigd; alleen legaal toegankelijk terrein gebruiken.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-cremat',
    shortName: 'Crémat',
    name: 'Miocene fossielvulling de Crémat · Cabrerets',
    lon: 1.665282,
    lat: 44.497069,
    driveMinutes: 64,
    driveKm: 48.3,
    inpgCode: 'MPY0265',
    stars: 2,
    materialOrAge: 'Mioceen; fossielhoudende karstvulling',
    evidenceNl: 'Tweesterren-INPG-mijnsite met paleontologisch belangrijke Miocene karstvulling.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historisch fossielhoudend, maar geen vrij toegankelijke oppervlaktevondsten bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-cregols',
    shortName: 'Crégols',
    name: 'Eocene fossielvullingen van Crégols',
    lon: 1.697924,
    lat: 44.455158,
    driveMinutes: 64,
    driveKm: 58.3,
    inpgCode: 'MPY0029',
    stars: 3,
    materialOrAge: 'Eoceen; fossielhoudende karstvullingen',
    evidenceNl: 'Driesterren-INPG-mijnsite met primair paleontologisch belang.',
    terrainNl: 'Voormalige ondergrondse mijn of phosphatière.',
    surfaceNl: 'Historisch bewezen fossielvulling; actuele oppervlaktevondsten niet gegarandeerd.',
    accessNl: 'Te bekijken via het officiële, zelfstandig toegankelijke karstpad van de igue de Crégols; blijf op de inrichting en ga geen schacht in.',
    collectingNl: 'Niet zoeken of meenemen zonder expliciete beheerdersbevestiging; controleer ook de grenzen van het geologisch reservaat.',
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://www.parc-causses-du-quercy.fr/explorez-les-causses-du-quercy/geotourisme/geosites/',
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-thezels',
    shortName: 'Thézels',
    name: 'Oligo-Miocene fossiellaag van Thézels',
    lon: 1.318003,
    lat: 44.269546,
    driveMinutes: 63,
    driveKm: 53.7,
    inpgCode: 'OCC0080',
    stars: 3,
    materialOrAge: 'Oligo-Mioceen',
    evidenceNl: 'Driesterren-INPG-ontsluiting met primair paleontologisch belang en bewezen fossilisatie.',
    terrainNl: 'Natuurlijke ontsluiting aan het oppervlak.',
    surfaceNl: 'Ja, de fossielhoudende laag ligt aan het oppervlak.',
    accessNl: 'Alleen observeren vanaf legaal toegankelijk terrein; geen verzamelbestemming.',
    collectingNl: 'Verboden: beschermd als géotope; niet zoeken, hakken of meenemen.',
    protectionNl: 'Arrêté préfectoral de protection de géotope (APPG).',
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: 'https://www.occitanie.developpement-durable.gouv.fr/IMG/pdf/2025-01__avispdg_rnniglot_46.pdf'
  },
  {
    id: 'fos-tempories',
    shortName: 'Les Tempories',
    name: 'Eocene fossielvulling des Tempories · Escamps',
    lon: 1.649533,
    lat: 44.360382,
    driveMinutes: 61,
    driveKm: 54.8,
    inpgCode: 'MPY0036',
    stars: 3,
    materialOrAge: 'Eoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-phosphatière met stratigrafisch belangrijke fossielvulling.',
    terrainNl: 'Voormalige ondergrondse fosfaatmijn met ingericht observatiepunt.',
    surfaceNl: 'De mijnstructuur is op het officiële pad zichtbaar; geen vrij verzamelen.',
    accessNl: 'Zelfstandig te bekijken via de officiële inrichting; op het pad blijven.',
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://www.parc-causses-du-quercy.fr/points-interets/phosphatiere-des-tempories/',
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-cloup-aural',
    shortName: 'Cloup d’Aural',
    name: 'Fossielvullingen du Cloup d’Aural · Bach',
    lon: 1.689675,
    lat: 44.350937,
    driveMinutes: 63,
    driveKm: 56.7,
    inpgCode: 'MPY0056',
    stars: 3,
    materialOrAge: 'Paleogeen; fossielrijke fosfaat- en karstvullingen',
    evidenceNl: 'Driesterren-INPG-phosphatière binnen het Quercy-complex, bekend om uitzonderlijk geconserveerde fossiele fauna.',
    terrainNl: 'Voormalige ondergrondse fosfaatmijn, ingericht als bezoekerssite.',
    surfaceNl: 'Fossielvulling en mijngeologie zijn in situ te zien; losse fossielen meenemen mag niet.',
    accessNl: 'Alleen via het officiële bezoekersparcours; opening en tickets vooraf controleren.',
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://phosphatieres.com/',
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-garouillas',
    shortName: 'Garouillas',
    name: 'Oligocene fossielvulling van Garouillas · Bach',
    lon: 1.650805,
    lat: 44.353182,
    driveMinutes: 74,
    driveKm: 55.3,
    inpgCode: 'MPY0004',
    stars: 3,
    materialOrAge: 'Oligoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-site met stratigrafisch belangrijke karstvulling.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historisch bewezen fossielvulling; geen openbare oppervlaktevondsten bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-valbro',
    shortName: 'Valbro 1',
    name: 'Oligocene fossielvulling van Valbro 1 · Bach',
    lon: 1.686495,
    lat: 44.362268,
    driveMinutes: 88,
    driveKm: 59.7,
    inpgCode: 'MPY0248',
    stars: 3,
    materialOrAge: 'Oligoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-mijnsite met primair paleontologisch belang.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historisch fossielrijk; actuele openbare oppervlaktevondsten niet bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-saleme',
    shortName: 'Salème',
    name: 'Eocene fossielvulling van Salème · Larnagol',
    lon: 1.789214,
    lat: 44.494258,
    driveMinutes: 83,
    driveKm: 71,
    inpgCode: 'MPY0037',
    stars: 3,
    materialOrAge: 'Eoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-mijnsite met primair paleontologisch belang.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historisch bewezen fossielvulling; geen openbare oppervlaktevondsten bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-calvignac',
    shortName: 'Calvignac',
    name: 'Bajocien fossielgroeve van Calvignac',
    lon: 1.779498,
    lat: 44.460912,
    driveMinutes: 77,
    driveKm: 67.1,
    inpgCode: 'MPY0212',
    stars: 2,
    materialOrAge: 'Bajocien, Midden-Jura; mariene platformafzetting',
    evidenceNl: 'Tweesterren-INPG-steengroeve met primair paleontologisch belang.',
    terrainNl: 'Oude steengroeve aan het oppervlak.',
    surfaceNl: 'Ja, fossielhoudende lagen zijn in de groeve ontsloten; actuele toegankelijkheid wisselt.',
    accessNl: 'Exacte groeve-toegang is niet als openbaar bevestigd. Blijf op eventuele gemarkeerde geositepaden.',
    collectingNl: RNN_NO_COLLECTING,
    protectionNl: RNN_STATUS,
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: RNN_URL
  },
  {
    id: 'fos-pech-fraysse',
    shortName: 'Pech du Fraysse',
    name: 'Chattien fossielvulling du Pech du Fraysse',
    lon: 1.73015,
    lat: 44.31222,
    driveMinutes: 69,
    driveKm: 62.2,
    inpgCode: 'MPY0291',
    stars: 3,
    materialOrAge: 'Chattien, Laat-Oligoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-karstnetwerk met grote stratigrafische waarde.',
    terrainNl: 'Natuurlijk ondergronds karstnetwerk.',
    surfaceNl: 'Ondergronds; geen bevestigde openbare oppervlaktevondsten.',
    accessNl: 'Geen publieke toegang bevestigd; grotten en schachten nooit zonder bevoegde begeleiding betreden.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-deveze',
    shortName: 'La Devèze',
    name: 'Chattien fossielvulling de la Devèze · Mouillac',
    lon: 1.689782,
    lat: 44.279282,
    driveMinutes: 85,
    driveKm: 64.5,
    inpgCode: 'MPY0288',
    stars: 3,
    materialOrAge: 'Chattien, Laat-Oligoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-mijnsite met primair paleontologisch belang.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historisch bewezen fossielvulling; geen openbare oppervlaktesituatie bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-itardies',
    shortName: 'Itardies',
    name: 'Rupelien fossielvulling d’Itardies · Caylus',
    lon: 1.694592,
    lat: 44.2757,
    driveMinutes: 85,
    driveKm: 64.5,
    inpgCode: 'MPY0287',
    stars: 3,
    materialOrAge: 'Rupelien, Vroeg-Oligoceen; fossielhoudende karstvulling',
    evidenceNl: 'Driesterren-INPG-mijnsite met stratigrafisch belangrijke vulling.',
    terrainNl: 'Voormalige ondergrondse mijn.',
    surfaceNl: 'Historisch bewezen fossielvulling; geen openbare oppervlaktesituatie bevestigd.',
    accessNl: NO_MINE_ENTRY,
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-combe-doree',
    shortName: 'Combe Dorée',
    name: 'Theropodensporen van Combe Dorée · Figeac',
    lon: 2.01694,
    lat: 44.621545,
    driveMinutes: 81,
    driveKm: 69.5,
    inpgCode: 'OCC0081',
    stars: 3,
    materialOrAge: 'Sinemurien, Vroeg-Jura; sporen van theropode dinosauriërs',
    evidenceNl: 'Driesterren-INPG-steengroeve met bewezen fossiele loopsporen.',
    terrainNl: 'Steengroeve met fossiele laag aan het oppervlak.',
    surfaceNl: 'Ja, de spoorlaag ligt bovengronds in de groeve.',
    accessNl: 'Geen openbare groeve-ingang bevestigd; alleen vanaf legaal toegankelijk terrein observeren.',
    collectingNl: 'Verboden: beschermd als géotope; niet zoeken, hakken of meenemen.',
    protectionNl: 'Arrêté préfectoral de protection de géotope (APPG).',
    coordinateBasisNl: INPG_CONTOUR,
    protectionUrl: 'https://www.occitanie.developpement-durable.gouv.fr/IMG/pdf/2025-01__avispdg_rnniglot_46.pdf'
  },
  {
    id: 'fos-lamothe-capdeville',
    shortName: 'Lamothe-Capdeville',
    name: 'Chattien fossielgroeve van Lamothe-Capdeville',
    lon: 1.379911,
    lat: 44.089437,
    driveMinutes: 80,
    driveKm: 88.6,
    inpgCode: 'MPY0365',
    stars: 1,
    materialOrAge: 'Chattien, Laat-Oligoceen',
    evidenceNl: 'Gevalideerde INPG-steengroeve met paleontologisch belang en bewezen fossilisatie.',
    terrainNl: 'Steengroeve aan het oppervlak.',
    surfaceNl: 'Ja, fossielhoudende lagen zijn bovengronds ontsloten; huidige vondsten niet gegarandeerd.',
    accessNl: 'Actieve status en publieke toegang zijn niet bevestigd; nooit zonder toestemming een groeve betreden.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-la-madeleine',
    shortName: 'La Madeleine',
    name: 'Aquitanien fossiellaag du vallon de La Madeleine · Moissac',
    lon: 1.038356,
    lat: 44.120609,
    driveMinutes: 87,
    driveKm: 75.2,
    inpgCode: 'MPY0354',
    stars: 1,
    materialOrAge: 'Aquitanien, Vroeg-Mioceen',
    evidenceNl: 'Gevalideerde INPG-ontsluiting met stratigrafisch belang en bewezen fossilisatie.',
    terrainNl: 'Natuurlijke dalwandontsluiting aan het oppervlak.',
    surfaceNl: 'Ja, de fossielhoudende laag is bovengronds ontsloten; zichtbaarheid kan seizoensmatig wisselen.',
    accessNl: 'Geen officiële bezoekersinrichting bevestigd; perceelgrenzen en natuurwaarden respecteren.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-monclar',
    shortName: 'Monclar-de-Quercy',
    name: 'Oligocene fossielgroeve van Monclar-de-Quercy',
    lon: 1.584349,
    lat: 43.970515,
    driveMinutes: 89,
    driveKm: 97.4,
    inpgCode: 'MPY0537',
    stars: 2,
    materialOrAge: 'Oligoceen; continentale fossielafzetting',
    evidenceNl: 'Tweesterren-INPG-steengroeve met stratigrafisch belangrijke fossielafzetting.',
    terrainNl: 'Steengroeve aan het oppervlak.',
    surfaceNl: 'Ja, de laag is bovengronds ontsloten; actuele groevestatus en vondstkans zijn onbekend.',
    accessNl: 'Geen openbare toegang bevestigd; groeve uitsluitend met expliciete toestemming betreden.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-cayrasses',
    shortName: 'Les Cayrasses',
    name: 'Kimmeridgien-fossielen in wijngaard Les Cayrasses · Cahors',
    lon: 1.474688,
    lat: 44.413323,
    driveMinutes: 41,
    driveKm: 31.2,
    materialOrAge: 'Kimmeridgien, Laat-Jura; ammonieten en oesters',
    evidenceNl: 'De wijnbouwer documenteert Ostrea virgula en ammonieten in de wijngaardbodem, waaronder een uitzonderlijk grote Aulacostephanus die in 2014 bij grondwerk zichtbaar werd.',
    terrainNl: 'Actieve privéwijngaard op fossielhoudende kalk- en mergelbodem.',
    surfaceNl: 'Fossielen zijn aantoonbaar bij bodembewerking aan het oppervlak gekomen; er is geen garantie dat nu los materiaal zichtbaar ligt.',
    accessNl: 'Alleen bezoeken na afspraak met de eigenaar. De marker geeft de wijngaard aan en is geen vrije zoek- of parkeerplek.',
    collectingNl: 'Een bezoek of wijnproeverij geeft geen toestemming om fossielen te zoeken of mee te nemen; vraag dit afzonderlijk en uitdrukkelijk.',
    coordinateBasisNl: 'Openbaar bezoekerspunt van het wijndomein; privéterrein.',
    officialUrl: 'https://www.tourisme-lot.com/offres/les-hauts-de-saint-georges-cayrasses-cahors-fr-885399/',
    sourceUrl: 'https://oulievindecahors.jimdofree.com/g%C3%A9ologie-fossile/'
  },
  {
    id: 'fos-la-toulzanie',
    shortName: 'La Toulzanie',
    name: 'Toarcien–Aalénien-serie van La Toulzanie · Saint-Martin-Labouval',
    lon: 1.74013,
    lat: 44.463553,
    driveMinutes: 71,
    driveKm: 63.6,
    inpgCode: 'MPY0179',
    stars: 2,
    materialOrAge: 'Toarcien–Aalénien, Jura; ammonieten en brachiopoden',
    evidenceNl: 'Gevalideerde INPG-ontsluiting. De geologische kaarttoelichting documenteert onder meer Pleydellia-ammonieten, brachiopoden en calcietgeoden in deze serie.',
    terrainNl: 'Natuurlijke stratigrafische ontsluiting aan het oppervlak.',
    surfaceNl: 'De gesteentelagen zijn bovengronds zichtbaar; actuele losse vondsten worden niet gegarandeerd.',
    accessNl: 'Geen officiële bezoekersingang. Controleer perceelgrenzen en verkeersveiligheid en bekijk alleen vanaf legaal toegankelijk terrein.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR,
    sourceUrl: 'https://www.researchgate.net/publication/281969912_Notice_explicative_Carte_geologique_de_la_France_150_000_feuille_Negrepelisse_931'
  },
  {
    id: 'fos-caylus-barre-pecten',
    shortName: 'Barre à Pecten · Caylus',
    name: 'Pliensbachien-profiel van de Barre à Pecten · Caylus',
    lon: 1.791121,
    lat: 44.221684,
    driveMinutes: 88,
    driveKm: 78.6,
    inpgCode: 'MPY1727',
    stars: 2,
    materialOrAge: 'Pliensbachien, Onder-Jura; fossielrijke Barre à Pecten',
    evidenceNl: 'Tweesterren-INPG-profiel. Wetenschappelijk onderzoek aan het zuidelijke Quercy beschrijft in deze Jura-serie fossielrijke niveaus met onder meer brachiopoden en ammonieten.',
    terrainNl: 'Wegontsluiting langs de D926.',
    surfaceNl: 'De gelaagdheid ligt aan het oppervlak; losse vondsten en veilige bereikbaarheid zijn niet gegarandeerd.',
    accessNl: 'Geen aangewezen stopplaats. Niet langs of op de rijbaan parkeren of lopen; alleen observeren waar dat aantoonbaar veilig en legaal kan.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR,
    sourceUrl: 'https://carnetsnatures.fr/articles-publies.html'
  },
  {
    id: 'fos-penne-granier',
    shortName: 'Château Granier · Penne',
    name: 'Typeprofiel van de Barre à Pecten · Penne',
    lon: 1.742067,
    lat: 44.07602,
    driveMinutes: 91,
    driveKm: 101.3,
    inpgCode: 'MPY1027',
    stars: 2,
    materialOrAge: 'Lias, Onder-Jura; fossielrijke Barre à Pecten',
    evidenceNl: 'Gevalideerd INPG-typeprofiel in een groeve; dezelfde zuidelijke Quercy-serie is wetenschappelijk beschreven met rijke Jura-fossielen.',
    terrainNl: 'Steengroeve aan het oppervlak.',
    surfaceNl: 'Fossielhoudende lagen zijn bovengronds ontsloten, maar de actuele groevestatus en vondstkans zijn onbekend.',
    accessNl: 'Geen publieke toegang bevestigd. Een actieve of verlaten groeve nooit zonder voorafgaande toestemming van eigenaar en exploitant betreden.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR,
    sourceUrl: 'https://carnetsnatures.fr/articles-publies.html'
  },
  {
    id: 'fos-saint-nazaire-valentane',
    shortName: 'Saint-Nazaire',
    name: 'Chattien-fossielgroeve van Saint-Nazaire-de-Valentane · Castelsagrat',
    lon: 0.988576,
    lat: 44.173054,
    driveMinutes: 75,
    driveKm: 65.9,
    inpgCode: 'MPY0359',
    stars: 1,
    materialOrAge: 'Chattien, Laat-Oligoceen; fossiele zoogdieren',
    evidenceNl: 'Gevalideerde INPG-groeve met paleontologisch en fossilisatiebelang; historische literatuur documenteert hier onder meer Anthracotherium-resten.',
    terrainNl: 'Steengroeve aan het oppervlak.',
    surfaceNl: 'De fossielhoudende vulling is bovengronds aangesneden; actuele zichtbaarheid en vondsten worden niet gegarandeerd.',
    accessNl: 'Publieke toegang en bedrijfsstatus zijn niet bevestigd. Betreed het groevegebied uitsluitend met expliciete toestemming.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR,
    sourceUrl: 'https://pascal-francis.inist.fr/vibad/index.php?action=getRecordDetail&idt=GEODEBRGMFR2070931&lang=en'
  },
  {
    id: 'fos-les-gervaises',
    shortName: 'Les Gervaises',
    name: 'Chattien-fossielvindplaats Les Gervaises · Saint-Paul-d’Espis',
    lon: 1.039088,
    lat: 44.147348,
    driveMinutes: 82,
    driveKm: 71.3,
    inpgCode: 'MPY0369',
    stars: 1,
    materialOrAge: 'Chattien, Laat-Oligoceen; fossielhoudende continentale afzetting',
    evidenceNl: 'Gevalideerde INPG-ontsluiting met paleontologisch belang en bewezen fossilisatie.',
    terrainNl: 'Natuurlijke ontsluiting aan het oppervlak.',
    surfaceNl: 'De fossielhoudende afzetting is bovengronds ontsloten; actuele losse vondsten worden niet gegarandeerd.',
    accessNl: 'Geen officiële bezoekersinrichting. Controleer eigendom, bereikbaarheid en eventuele lokale bescherming voordat je het terrein nadert.',
    collectingNl: PERMISSION_ONLY,
    coordinateBasisNl: INPG_CONTOUR
  },
  {
    id: 'fos-beon',
    shortName: 'Béon',
    name: 'Paléosite de Béon · Montréal-du-Gers',
    lon: 0.22214,
    lat: 43.93646,
    driveMinutes: 160,
    driveKm: 151.3,
    inpgCode: 'MPY0440',
    stars: 3,
    materialOrAge: 'Burdigalien, circa 17 miljoen jaar; rijke gewervelde fauna',
    evidenceNl: 'Driesterren-INPG-site. Het Muséum de Toulouse noemt Béon een van Europa’s grote vindplaatsen, met meer dan negentig gewervelde soorten.',
    terrainNl: 'Fossielgroeve aan het oppervlak met officiële bezoekersmogelijkheid.',
    surfaceNl: 'Ja, de originele vindlaag en opgraving zijn bovengronds zichtbaar tijdens publieksmomenten.',
    accessNl: 'Uitzonderlijke dagtocht buiten 1½ uur; controleer rondleidingen en reservering bij de officiële beheerder.',
    collectingNl: 'Niet verzamelen; dit is een wetenschappelijke opgravings- en bezoekerssite.',
    protectionNl: 'Beheerde paleontologische vindplaats.',
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://museum.toulouse-metropole.fr/montreal-du-gers/'
  },
  {
    id: 'fos-sansan',
    shortName: 'Sansan',
    name: 'Paléosite de Sansan · Campané',
    lon: 0.622394,
    lat: 43.524258,
    driveMinutes: 176,
    driveKm: 159.9,
    inpgCode: 'MPY0461',
    stars: 3,
    materialOrAge: 'Langhien, Midden-Mioceen; internationaal beroemde fossiele fauna',
    evidenceNl: 'Driesterren-INPG-site. Het MNHN rekent Sansan tot de wereldwijd bekendste fossielvindplaatsen; er zijn honderden soorten gedocumenteerd.',
    terrainNl: 'Bovengrondse ontsluiting met officieel paleontologisch wandelpad.',
    surfaceNl: 'Ja, de vindplaats is aan het oppervlak te bekijken langs het bezoekerspad.',
    accessNl: 'Uitzonderlijke dagtocht buiten 1½ uur; volg uitsluitend het officiële parcours en controleer bezoekersinformatie.',
    collectingNl: 'Niet verzamelen of buiten het pad prospecteren; alleen observeren.',
    protectionNl: 'Beheerde paleontologische vindplaats.',
    coordinateBasisNl: INPG_CONTOUR,
    officialUrl: 'https://www.paleosite-sansan.com/',
    protectionUrl: 'https://www.mnhn.fr/fr/paleosite-de-sansan'
  }
]

export const THEDIRAC_MINERALS_LAYER_NAME = `Mineralen (${THEDIRAC_MINERAL_SITES.length})`
export const THEDIRAC_FOSSILS_LAYER_NAME = `Fossielen (${THEDIRAC_FOSSIL_SITES.length})`

export function inpgSiteUrl(code: string) {
  return `https://inpn.mnhn.fr/site/inpg/${code}`
}

export { INPG_DATASET_URL }
